/**
 * MetabolicInsulinTwinPage — iter628 — Метаболитно-инсулинов близнак (12-ти).
 * HOMA-IR + TG/HDL + ApoB/ApoA1 + HbA1c (от Lab Twin).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Activity, Plus } from "lucide-react";
import { toast } from "sonner";
import ProfileContextStrip from "@/components/shared/ProfileContextStrip";
import InsulinAxisCard from "@/components/shared/InsulinAxisCard";
import { twinLabel } from "@/lib/twinCatalog";

import { MobileTopBar } from "@/components/MobileTopBar";
export default function MetabolicInsulinTwinPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    fasting_glucose_mmol_l: "", fasting_insulin_uiu_ml: "",
    triglycerides_mmol_l: "", hdl_mmol_l: "",
    apob_g_l: "", apoa1_g_l: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/metabolic-insulin-twin/snapshot`);
      setSnap(data);
    } catch {
      toast.error("Грешка при зареждане.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [API]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form)
          .filter(([, v]) => v !== "" && v !== null)
          .map(([k, v]) => [k, parseFloat(v)])
      );
      if (Object.keys(payload).length === 0) {
        toast.error("Попълни поне едно поле."); return;
      }
      await axios.post(`${API}/metabolic-insulin-twin/log`, payload);
      window.dispatchEvent(new Event("metabolic-log-changed"));
      window.dispatchEvent(new Event("twin-score-changed"));
      toast.success("Записано!");
      setForm({ fasting_glucose_mmol_l: "", fasting_insulin_uiu_ml: "", triglycerides_mmol_l: "", hdl_mmol_l: "", apob_g_l: "", apoa1_g_l: "" });
      load();
    } catch {
      toast.error("Грешка при запис.");
    } finally {
      setSaving(false);
    }
  };

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`} data-testid="metabolic-twin-page">
      <MobileTopBar title="Обмяна" fallbackTo="/dashboard" testid="metabolic_insulin-twin-mobile-topbar" />
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        {/* iter1017 — профилът захранва близнака */}
        <ProfileContextStrip domain="metabolic" isDark={isDark} className="mb-4" />
        <button onClick={() => navigate("/dashboard")} className="text-xs opacity-60 hover:opacity-100 mb-3 flex items-center gap-1" data-testid="back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Към таблото
        </button>

        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-5 h-5 text-lime-500" />
          <h1 className="text-xl font-bold">{twinLabel("metabolic_insulin")}</h1>
        </div>
        <p className={`text-xs mb-4 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
          HOMA-IR, TG/HDL и ApoB/ApoA1 разкриват ранна инсулинова резистентност и кардиометаболитен риск — преди да се появи на стандартните изследвания.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-lime-500" /></div>
        ) : (
          <>
            <div className={`p-5 rounded-2xl border-2 mb-4 ${cardCls}`} data-testid="metabolic-snapshot">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-4xl font-bold" data-testid="metabolic-score">{snap?.composite ?? "—"}</span>
                <span className="text-xs opacity-60">/100</span>
                {snap?.homa_ir != null && (
                  <span className={`ml-auto text-xs px-2 py-1 rounded-md font-bold ${snap.homa_ir < 1.5 ? "bg-emerald-500 text-white" : snap.homa_ir < 2.5 ? "bg-amber-500 text-white" : "bg-rose-500 text-white"}`} data-testid="homa-ir">
                    HOMA-IR {snap.homa_ir}
                  </span>
                )}
              </div>
              <p className="text-sm leading-relaxed mt-2" data-testid="metabolic-interpretation">{snap?.interpretation_bg}</p>
            </div>

            {/* iter1055 — една инсулинова ос: DIL (храна) + HOMA-IR (кръв) */}
            <div className="mb-4">
              <InsulinAxisCard isDark={isDark} />
            </div>

            <div className={`p-5 rounded-2xl border mb-4 ${cardCls}`} data-testid="metabolic-log-form">
              <h2 className="font-bold text-sm mb-3 flex items-center gap-1"><Plus className="w-4 h-4" /> Лог метаболитен панел</h2>
              <div className="grid grid-cols-2 gap-3 mb-3">
                {[
                  { k: "fasting_glucose_mmol_l", label: "Fasting глюкоза (mmol/L)", placeholder: "5.0" },
                  { k: "fasting_insulin_uiu_ml", label: "Fasting инсулин (μIU/mL)", placeholder: "6" },
                  { k: "triglycerides_mmol_l", label: "Триглицериди (mmol/L)", placeholder: "1.0" },
                  { k: "hdl_mmol_l", label: "HDL (mmol/L)", placeholder: "1.4" },
                  { k: "apob_g_l", label: "ApoB (g/L)", placeholder: "0.8" },
                  { k: "apoa1_g_l", label: "ApoA1 (g/L)", placeholder: "1.5" },
                ].map(f => (
                  <label key={f.k} className="flex flex-col gap-1">
                    <span className="text-[10px] opacity-70">{f.label}</span>
                    <input type="number" step="0.1" placeholder={f.placeholder} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                      data-testid={`metabolic-input-${f.k}`}
                      className={`px-2 py-1.5 rounded-md border text-sm ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`} />
                  </label>
                ))}
              </div>
              <Button onClick={save} disabled={saving} className="bg-lime-600 hover:bg-lime-700 text-white w-full" data-testid="metabolic-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Запиши панел
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
